import React, { useEffect, useState, useMemo } from "react";
import BreadCrumsHeader from "../../common/BreadCrums";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
} from "@mui/material";
import Pagination from "../../common/TablePagination";
import CreateNewButton from "../../common/CreateNewButton";
import { useDispatch, useSelector } from "react-redux";
import Loader from "../../common/Loader";
import {
  getFundraiserReferrals,
  getMerchantById,
} from "../../../services/fundraiserService";
import FilterPanel from "../../common/FilterPanel";
import { Dropdown } from "antd";
import SearchBar from "../../common/SearchBar";
import { ReactComponent as DownArrow } from "../../../Assets/Icons/greyDownArrow.svg";
import { ReactComponent as FiltersIcon } from "../../../Assets/Icons/filtersIcon.svg";
import { ReactComponent as PendingIcon } from "../../../Assets/Icons/pendingIcon.svg";
import { ReactComponent as AcceptedIcon } from "../../../Assets/Icons/acceptedIcon.svg";

import PopupContent from "./PopupContent";
import DetailsModal from "../../common/DetailsModal";
import { separateDateTimeAndConvertToLocale } from "../../../utils/utilFunctions";
import debounce from "lodash.debounce";

const FundraiserReferralListTable = () => {
  const dispatch = useDispatch();
  const loading = useSelector((state) => state?.fundraiserReducer?.loading);
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [merchantDetails, setMerchantDetails] = useState({});

  const items = [
    {
      title: "Refer a Merchant",
    },
    {
      title: "Referral List",
    },
  ];

  const columns = [
    {
      name: "No.",
    },
    {
      name: "Name",
    },
    {
      name: "Date",
    },
    {
      name: "Email",
    },
    {
      name: "Contact",
    },
    {
      name: "Status",
    },
    {
      name: "Actions",
    },
  ];

  const [allReferrals, setAllReferrals] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [count, setCount] = useState(0);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [filterParams, setFilterParams] = useState({
    status: null,
    duration: null,
  });

  const getAllReferrals = async (search) => {
    // Create params object with all required parameters
    const params = {
      page: currentPage,
      limit: 10,
      search: search || null,
      status: filterParams.status?.toLowerCase() || null,
      duration: filterParams.duration?.toLowerCase() || null,
    };

    // Remove null values to avoid sending empty parameters
    Object.keys(params).forEach((key) =>
      params[key] === null ? delete params[key] : {}
    );

    const refferralPromise = await dispatch(
      getFundraiserReferrals(params)
    ).unwrap();

    if (refferralPromise?.success === true) {
      setAllReferrals(refferralPromise?.data);
      setCurrentPage(refferralPromise?.currentPage);
      setTotalPages(refferralPromise?.totalPages);
      setCount(refferralPromise?.count);
    }
  };

  useEffect(() => {
    getAllReferrals(searchQuery);
  }, [currentPage, filterParams]);

  const fetchMerchantDetails = async (merchantId) => {
    try {
      const merchantDetailsPromise = await dispatch(
        getMerchantById({ id: merchantId })
      )?.unwrap();
      if (merchantDetailsPromise?.success === true) {
        setMerchantDetails(merchantDetailsPromise?.data);
        setIsDetailOpen(true);
      }
    } catch (error) {
      console.error("Error fetching offer details:", error);
    }
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleCancel = () => {
    setOpen(false);
  };

  const handleOpenChange = (flag) => {
    setOpen(flag);
  };

  const [filterData, setFilterData] = useState([
    {
      id: "duration",
      title: "Duration",
      options: ["Today", "This Week", "This Month", "This Year"],
      defaultValue: "Today",
      multiSelect: false,
    },
    {
      id: "status",
      title: "Status",
      options: ["Accepted", "Pending"],
      defaultValue: "Accepted",
      multiSelect: false,
    },
  ]);

  // Create initial state from filter data
  const createInitialState = (filters) => {
    return filters.reduce((acc, filter) => {
      if (filter.multiSelect) {
        // For multi-select, initialize as array
        acc[filter.id] = Array.isArray(filter.defaultValue)
          ? filter.defaultValue
          : [];
      } else {
        // For single select, initialize as single value
        acc[filter.id] = filter.defaultValue;
      }
      return acc;
    }, {});
  };

  // Initialize selections with the initial state
  const [selections, setSelections] = useState(createInitialState(filterData));

  const handleSelectionChange = (filterId, value) => {
    setSelections((prev) => ({
      ...prev,
      [filterId]: value,
    }));
  };

  // Update the handleReset function
  const handleReset = () => {
    const initialState = createInitialState(filterData);
    setSelections(initialState);

    // Reset filter params to trigger new API call
    setFilterParams({
      status: null,
      duration: null,
    });
  };

  const debouncedSearch = useMemo(
    () =>
      debounce((query) => {
        getAllReferrals(query);
      }, 500),
    [filterParams]
  );

  const handleSearchChange = (value) => {
    setSearchQuery(value);
    debouncedSearch(value);
  };

  const handleApply = () => {
    console.log("Applying filters:", selections);

    // Update filter params to trigger new API call
    setFilterParams({
      status: selections.status,
      duration: selections.duration,
    });

    setOpen(false);
  };

  const openPopup = (merchantId) => {
    fetchMerchantDetails(merchantId);
  };

  const closePopup = () => {
    setIsDetailOpen(false);
  };

  return (
    <>
      {loading === true && <Loader />}
      {isDetailOpen && (
        <DetailsModal
          handleClose={closePopup}
          content={<PopupContent merchantDetails={merchantDetails} />}
          popupClassName="p-6 w-[335px] sm:w-[600px] md:w-[728px]"
        />
      )}
      {/* Header Row: Title, Breadcrumbs, Button */}
      <div className="flex flex-row items-center justify-between mt-2 mb-6">
        <div>
          <BreadCrumsHeader heading="Referral List" items={items} form={true} />
        </div>
        <CreateNewButton
          text={"Refer New Merchant"}
          navigateTo={"/fundraiser/refer-merchant"}
          iconClassName="sm:left-[1%] md:left-[6%] bottom-[30%]"
        />
      </div>

      {/* Search and Filter Row */}
      <div className="w-full flex justify-between items-center mt-2 mb-2">
        <SearchBar
          parentDivClass="w-[65%] md:w-[40%] lg:w-[35%] xl:w-[30%]"
          divClassName="relative"
          inputClassName="searchBar w-full p-3 text-[14px] font-poppins font-[400] outline-none"
          iconClassName="absolute sm:right-[5%] sm:bottom-[25%] lg:right-[3%]"
          placeholder="Search"
          value={searchQuery}
          handleChange={(e) => handleSearchChange(e.target.value)}
        />
        <Dropdown
          overlay={
            <FilterPanel
              filterData={filterData}
              selections={selections}
              handleApply={handleApply}
              handleCancel={handleCancel}
              handleReset={handleReset}
              handleSelectionChange={handleSelectionChange}
            />
          }
          trigger={["click"]}
          open={open}
          onOpenChange={handleOpenChange}
          placement="bottomRight"
          overlayStyle={{
            minWidth: "320px",
            maxHeight: "80vh",
            zIndex: 1000,
          }}
          overlayClassName="filter-dropdown-overlay"
        >
          <button className="flex justify-center items-center gap-2 border border-[#00ADEF] rounded-md p-3 text-lg font-medium outline-none h-[40px]">
            <div className="flex items-center gap-2">
              <FiltersIcon className="w-[20px] h-[20px]" />
              <span className="font-[500] text-[14px] font-poppins leading-[22px] text-[#8C8C8C]">
                Filters
              </span>
            </div>
            <DownArrow />
          </button>
        </Dropdown>
      </div>

      <div className="col-span-12 mt-4">
        <TableContainer style={{ width: "100%", overflowX: "auto" }}>
          <Table
            sx={{
              minWidth: "1000px",
              borderCollapse: "separate",
              borderSpacing: "0px 8px",
            }}
            aria-label="simple table"
          >
            <TableHead className="bg-[#00ADEF]">
              <TableRow
                className="h-[50px]"
                sx={{ boxShadow: "0px 4px 4px 0px #00000040" }}
              >
                {columns.map((column) => {
                  return <TableCell key={column.name} className="!text-[#FFFFFF]">{column.name}</TableCell>;
                })}
              </TableRow>
            </TableHead>

            <TableBody>
              {allReferrals?.map((row, index) => {
                return (
                  <TableRow
                    className="h-[50px]"
                    sx={{
                      boxShadow: "0px 4px 4px 0px #00000040",
                      backgroundColor:
                        (index + 1) % 2 !== 0 ? "#F5F5F5" : "#FFFFFF",
                    }}
                    key={index}
                  >
                    <TableCell className="max-w-[50px]">{index + 1}</TableCell>
                    <TableCell
                      // title={row?.merchant?.businessName || ""}
                      title={row?.merchant?.businessName || row?.name || "-"}
                      className="max-w-[50px] truncate"
                    >
                      {row?.merchant?.businessName || row?.name || "-"}
                    </TableCell>
                    <TableCell
                      title={row?.createdAt || ""}
                      className="max-w-[150px] truncate"
                    >
                      {separateDateTimeAndConvertToLocale(row?.createdAt)
                        ?.localDate || "-"}
                    </TableCell>
                    <TableCell
                      title={row?.user?.email || row?.email || "-"}
                      className="max-w-[100px] truncate"
                    >
                      {row?.user?.email || row?.email || "-"}
                    </TableCell>
                    <TableCell
                      title={
                        row?.merchant?.contactPersonNumber ||
                        row?.contactNumber ||
                        "-"
                      }
                      className="max-w-[70px] truncate"
                    >
                      {row?.merchant?.contactPersonNumber ||
                        row?.contactNumber ||
                        "-"}
                    </TableCell>
                    <TableCell
                      title={row?.status || ""}
                      className="max-w-[50px] truncate"
                    >
                      {row?.status == "accepted" ? (
                        <AcceptedIcon />
                      ) : (
                        <PendingIcon />
                      )}
                    </TableCell>
                    <TableCell className="max-w-[50px] truncate">
                      <button
                        className={`flex justify-center items-center border rounded-[6px] p-3 h-[34px] ${row?.status == "pending"
                          ? "border-[#979797] text-[#979797] cursor-not-allowed"
                          : "border-[#00ADEF] text-[#00ADEF]"
                          }`}
                        onClick={() => openPopup(row?.merchant?._id)}
                        disabled={row?.status == "pending"}
                      >
                        <span className="font-[600] text-[14px] font-poppins leading-[22px]">
                          View Details
                        </span>
                      </button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <Pagination
          totalPages={totalPages}
          handlePageChange={handlePageChange}
        />
      </div>
    </>
  );
};

export default FundraiserReferralListTable;
